import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  GameMode,
  MinecraftItemId,
  Player,
  PlayerPlatform,
  ServerSummary,
  SessionResponse,
  WhitelistEntry,
  AgentMessage,
  AgentSettings,
  SessionUser,
} from '@crafty-companion/shared';
import { GAME_MODES, MINECRAFT_ITEMS } from '@crafty-companion/shared';
import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import {
  ApiClientError,
  api,
  loginOptions,
  loginVerify,
  registerOptions,
  registerVerify,
  session,
} from './api.ts';
import { shouldRefreshOnForeground } from './foreground.ts';
import { findMinecraftItems } from './items.ts';
import {
  getAgentMessages,
  clearAgent,
  getAgentSettings,
  setAgentEnabled,
  streamAgentMessage,
} from './agent-api.ts';

type DetailTab = 'players' | 'whitelist' | 'announce' | 'logs' | 'command';
type Mutation = (
  key: string,
  action: () => Promise<unknown>,
  successMessage?: string | null,
) => void;
type Toast = { kind: 'success' | 'error'; message: string };

function relativeTime(timestamp: number) {
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
    Math.max(-15, Math.round((timestamp - Date.now()) / 60_000)),
    'minute',
  );
}

function memoryInMiB(value: string) {
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^([\d.]+)\s*(K|M|G|T)?B?$/i);
  if (!match) return 0;
  const amount = Number(match[1]);
  const unit = match[2]?.toUpperCase() ?? 'M';
  return amount * ({ K: 1 / 1024, M: 1, G: 1024, T: 1024 * 1024 }[unit] ?? 1);
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Crafty Companion render failed', error, info);
  }

  override render() {
    if (this.state.error) {
      return (
        <main className="fatal-error">
          <div className="brand-mark large">
            <i />
            <i />
            <i />
          </div>
          <p className="eyebrow">Display error</p>
          <h1>The control room hit a snag.</h1>
          <p>Reload the app to fetch the latest version. Your servers were not changed.</p>
          <button className="primary-button" onClick={() => location.reload()}>
            Reload app
          </button>
        </main>
      );
    }
    return this.props.children;
  }
}

export function SafeApp() {
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}

export function App() {
  const [auth, setAuth] = useState<SessionResponse | null>(null);
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);
  const [foregroundRefresh, setForegroundRefresh] = useState(0);
  const [view, setView] = useState<'control' | 'assistant'>('control');
  const serverRefreshInFlight = useRef<Promise<void> | null>(null);
  const showIosInstall =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.matchMedia('(display-mode: standalone)').matches;

  const refreshSession = useCallback(async () => setAuth(await session()), []);
  const refreshServers = useCallback(() => {
    if (serverRefreshInFlight.current) return serverRefreshInFlight.current;
    const refresh = (async () => {
      try {
        const result = await api.servers();
        setServers(result.servers);
        setSelectedId((current) => current || result.servers[0]?.id || '');
        setError('');
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not load servers');
      }
    })();
    serverRefreshInFlight.current = refresh;
    void refresh.finally(() => {
      if (serverRefreshInFlight.current === refresh) serverRefreshInFlight.current = null;
    });
    return refresh;
  }, []);

  useEffect(() => {
    void refreshSession();
    const listener = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', listener);
    return () => window.removeEventListener('beforeinstallprompt', listener);
  }, [refreshSession]);

  useEffect(() => {
    if (!auth?.user) return;
    void refreshServers();
    const timer = window.setInterval(() => void refreshServers(), 5000);
    return () => window.clearInterval(timer);
  }, [auth?.user, refreshServers]);

  useEffect(() => {
    if (!auth?.user) return;
    let lastRefreshAt = 0;
    const refreshWhenForegrounded = () => {
      const now = Date.now();
      if (!shouldRefreshOnForeground(document.visibilityState, lastRefreshAt, now)) return;
      lastRefreshAt = now;
      void refreshServers();
      setForegroundRefresh((current) => current + 1);
    };
    document.addEventListener('visibilitychange', refreshWhenForegrounded);
    window.addEventListener('focus', refreshWhenForegrounded);
    window.addEventListener('pageshow', refreshWhenForegrounded);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenForegrounded);
      window.removeEventListener('focus', refreshWhenForegrounded);
      window.removeEventListener('pageshow', refreshWhenForegrounded);
    };
  }, [auth?.user, refreshServers]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), toast.kind === 'success' ? 2_000 : 4_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function mutation(
    key: string,
    action: () => Promise<unknown>,
    successMessage: string | null = 'Done',
  ) {
    setBusy(key);
    setToast(null);
    try {
      await action();
      await refreshServers();
      if (successMessage) setToast({ kind: 'success', message: successMessage });
    } catch (caught) {
      setToast({
        kind: 'error',
        message: caught instanceof Error ? caught.message : 'Operation failed',
      });
    } finally {
      setBusy(null);
    }
  }

  if (!auth) return <Splash />;
  if (!auth.user) return <AuthScreen auth={auth} onComplete={refreshSession} />;

  const selected = servers.find((server) => server.id === selectedId) ?? servers[0];
  const craftyMemoryMiB = servers.reduce((total, server) => total + memoryInMiB(server.memory), 0);
  const memoryPressure = craftyMemoryMiB >= 4352;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <div>
            <strong>Crafty Companion</strong>
            <span>Piromania control room</span>
          </div>
        </div>
        <div className="segmented-nav">
          <button className={view === 'control' ? 'active' : ''} onClick={() => setView('control')}>
            Control room
          </button>
          <button
            className={view === 'assistant' ? 'active' : ''}
            onClick={() => setView('assistant')}
          >
            Assistant
          </button>
        </div>
        <div className="topbar-actions">
          {installPrompt && (
            <button className="quiet-button" onClick={() => (installPrompt as any).prompt()}>
              Install
            </button>
          )}
          <button
            className="avatar-button"
            title={`Signed in as ${auth.user.displayName}`}
            onClick={() =>
              void mutation(
                'logout',
                async () => {
                  await api.logout();
                  await refreshSession();
                },
                null,
              )
            }
          >
            {auth.user.displayName.slice(0, 1).toUpperCase()}
          </button>
        </div>
      </header>

      <main>
        {memoryPressure && (
          <div className="warning-banner">
            <span>Memory pressure is high.</span> Consider stopping an idle server before the Crafty
            container reaches its limit.
          </div>
        )}
        {showIosInstall && (
          <div className="info-banner">
            On iPhone, tap <strong>Share</strong>, then <strong>Add to Home Screen</strong> to
            install the app.
          </div>
        )}
        {error && (
          <button className="error-banner" onClick={() => setError('')}>
            {error}
          </button>
        )}

        {view === 'assistant' ? (
          <AssistantPanel user={auth.user} mutate={mutation} setToast={setToast} />
        ) : (
          <>
            <section className="server-grid" aria-label="Minecraft servers">
              {servers.map((server) => (
                <ServerCard
                  key={server.id}
                  server={server}
                  active={server.id === selected?.id}
                  busy={busy === server.id}
                  onSelect={() => setSelectedId(server.id)}
                  onAction={(action) =>
                    void mutation(
                      server.id,
                      () => api.serverAction(server.id, action),
                      `${server.name} ${action === 'start' ? 'started' : 'stopped'}`,
                    )
                  }
                />
              ))}
            </section>

            {selected && (
              <ServerControls
                server={selected}
                busy={busy}
                foregroundRefresh={foregroundRefresh}
                mutate={(key, action, successMessage) => void mutation(key, action, successMessage)}
              />
            )}

            {auth.user.isOwner && (
              <OwnerPanel
                mutate={(key, action, successMessage) => void mutation(key, action, successMessage)}
              />
            )}
          </>
        )}
      </main>
      {toast && (
        <div
          className={`action-toast ${toast.kind}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          <span aria-hidden="true">{toast.kind === 'success' ? '✓' : '!'}</span>
          {toast.message}
        </div>
      )}
    </div>
  );
}

function Splash() {
  return (
    <div className="splash">
      <div className="brand-mark large">
        <i />
        <i />
        <i />
      </div>
      <p>Loading your worlds…</p>
    </div>
  );
}

function AuthScreen({
  auth,
  onComplete,
}: {
  auth: SessionResponse;
  onComplete: () => Promise<void>;
}) {
  const query = new URLSearchParams(location.search);
  const inviteToken = query.get('invite') ?? undefined;
  const mode = auth.bootstrapRequired || inviteToken ? 'register' : 'login';
  const [displayName, setDisplayName] = useState('');
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode === 'register') {
        const setup = await registerOptions({
          displayName,
          ...(inviteToken ? { inviteToken } : {}),
          ...(auth.bootstrapRequired ? { bootstrapToken } : {}),
        });
        const response = await startRegistration({ optionsJSON: setup.options });
        await registerVerify(setup.challengeId, response);
        history.replaceState({}, '', '/');
      } else {
        const setup = await loginOptions(displayName);
        const response = await startAuthentication({ optionsJSON: setup.options });
        await loginVerify(setup.challengeId, response);
      }
      await onComplete();
    } catch (caught) {
      setError(
        caught instanceof ApiClientError || caught instanceof Error
          ? caught.message
          : 'Passkey operation failed',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-art">
        <div className="pixel-orbit">
          <span className="cube cube-one" />
          <span className="cube cube-two" />
          <span className="cube cube-three" />
        </div>
        <p className="eyebrow">Private by design</p>
        <h1>
          Your Minecraft worlds,
          <br />
          one tap away.
        </h1>
        <p>No passwords to remember. Your device is your key.</p>
      </section>
      <section className="auth-card">
        <div className="brand">
          <span className="brand-mark">
            <i />
            <i />
            <i />
          </span>
          <strong>Crafty Companion</strong>
        </div>
        <div>
          <p className="eyebrow">{mode === 'register' ? 'New device' : 'Welcome back'}</p>
          <h2>{mode === 'register' ? 'Create your passkey' : 'Open the control room'}</h2>
          <p className="muted">
            {mode === 'register'
              ? 'Choose the name your family will recognize.'
              : 'Enter your account name, then use your device passkey.'}
          </p>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Display name
            <input
              autoComplete="username webauthn"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Miklos"
              maxLength={40}
              required
            />
          </label>
          {mode === 'register' && auth.bootstrapRequired && (
            <label>
              Bootstrap token
              <input
                type="password"
                value={bootstrapToken}
                onChange={(event) => setBootstrapToken(event.target.value)}
                required
              />
            </label>
          )}
          {error && <div className="inline-error">{error}</div>}
          <button className="primary-button" disabled={busy}>
            {busy
              ? 'Waiting for your device…'
              : mode === 'register'
                ? 'Create passkey'
                : 'Use passkey'}
          </button>
        </form>
        {!auth.bootstrapRequired && !inviteToken && (
          <p className="muted invitation-hint">New here? Ask the owner for an invitation link.</p>
        )}
      </section>
    </main>
  );
}

function ServerCard({
  server,
  active,
  busy,
  onSelect,
  onAction,
}: {
  server: ServerSummary;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onAction: (action: 'start' | 'stop') => void;
}) {
  return (
    <article className={`server-card ${active ? 'active' : ''}`} onClick={onSelect}>
      <div className="server-card-head">
        <span className={`status-dot ${server.running ? 'online' : ''}`} />
        <span>{server.running ? 'Online' : server.waitingStart ? 'Starting' : 'Offline'}</span>
        <span className="mode-badge">{server.mode}</span>
      </div>
      <h2>{server.name}</h2>
      <div className="server-stats">
        <div>
          <strong>{server.online}</strong>
          <span>players</span>
        </div>
        <div>
          <strong>{server.memory}</strong>
          <span>memory</span>
        </div>
      </div>
      <button
        className={server.running ? 'danger-button' : 'primary-button'}
        disabled={busy || server.waitingStart}
        onClick={(event) => {
          event.stopPropagation();
          if (!server.running || confirm(`Gracefully stop ${server.name}?`)) {
            onAction(server.running ? 'stop' : 'start');
          }
        }}
      >
        {busy ? 'Working…' : server.running ? 'Stop server' : 'Start server'}
      </button>
    </article>
  );
}

function ServerControls({
  server,
  busy,
  foregroundRefresh,
  mutate,
}: {
  server: ServerSummary;
  busy: string | null;
  foregroundRefresh: number;
  mutate: Mutation;
}) {
  const [tab, setTab] = useState<DetailTab>('players');
  return (
    <section className="control-panel">
      <div className="control-heading">
        <div>
          <p className="eyebrow">Manage {server.name}</p>
          <h2>World controls</h2>
        </div>
        <label className="mode-select">
          Default mode
          <select
            value={server.mode}
            disabled={!server.running || busy !== null}
            onChange={(event) =>
              mutate(
                `mode-${server.id}`,
                () => api.setMode(server.id, event.target.value as GameMode),
                'Default game mode updated',
              )
            }
          >
            {GAME_MODES.map((mode) => (
              <option key={mode}>{mode}</option>
            ))}
          </select>
        </label>
      </div>
      {!server.running && (
        <p className="offline-note">Start this server to change its mode or whitelist.</p>
      )}
      <nav className="tabs">
        {(['players', 'whitelist', 'announce', 'logs', 'command'] as DetailTab[]).map((item) => (
          <button className={tab === item ? 'active' : ''} onClick={() => setTab(item)} key={item}>
            {item}
          </button>
        ))}
      </nav>
      {tab === 'players' && <PlayersPanel server={server} mutate={mutate} />}
      {tab === 'whitelist' && (
        <WhitelistPanel server={server} mutate={mutate} foregroundRefresh={foregroundRefresh} />
      )}
      {tab === 'announce' && <AnnouncementPanel server={server} mutate={mutate} busy={busy} />}
      {tab === 'logs' && <LogsPanel server={server} foregroundRefresh={foregroundRefresh} />}
      {tab === 'command' && <CommandPanel server={server} mutate={mutate} busy={busy} />}
    </section>
  );
}

function AnnouncementPanel({
  server,
  mutate,
  busy,
}: {
  server: ServerSummary;
  mutate: Mutation;
  busy: string | null;
}) {
  const [text, setText] = useState('');
  const normalized = text.trim();

  return (
    <form
      className="announcement-panel"
      onSubmit={(event) => {
        event.preventDefault();
        if (!normalized) return;
        mutate(
          `announce-${server.id}`,
          async () => {
            await api.announce(server.id, normalized);
            setText('');
          },
          'Announcement shown to all players',
        );
      }}
    >
      <div>
        <p className="eyebrow">Big title</p>
        <h3>Announce to everyone</h3>
        <p className="tool-hint">
          Shows gold, bold text in the center of every online player’s screen.
        </p>
      </div>
      <label>
        Announcement
        <input
          type="text"
          value={text}
          onChange={(event) => setText(event.target.value.replaceAll(/[\r\n]/g, ''))}
          placeholder="Dinner is ready!"
          maxLength={120}
          autoComplete="off"
          disabled={!server.running || busy !== null}
          required
        />
      </label>
      <p className="announcement-preview" aria-live="polite">
        <strong>{normalized || 'Your announcement'}</strong>
        <span>{normalized.length}/120</span>
      </p>
      <button className="primary-button" disabled={!server.running || busy !== null || !normalized}>
        Show announcement
      </button>
    </form>
  );
}

// Procedural pseudo-random noise generator
function hash2D(x: number, y: number) {
  const h = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453123;
  return h - Math.floor(h);
}

function noise2D(x: number, y: number) {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;

  const ux = fx * fx * (3.0 - 2.0 * fx);
  const uy = fy * fy * (3.0 - 2.0 * fy);

  const a = hash2D(ix, iy);
  const b = hash2D(ix + 1, iy);
  const c = hash2D(ix, iy + 1);
  const d = hash2D(ix + 1, iy + 1);

  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

function fbm2D(x: number, y: number, octaves = 3) {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1.0;
  for (let i = 0; i < octaves; i++) {
    value += amplitude * noise2D(x * frequency, y * frequency);
    frequency *= 2.0;
    amplitude *= 0.5;
  }
  return value;
}

function getTerrainColor(value: number, dimension: string) {
  if (dimension === 'minecraft:the_nether') {
    if (value < 0.28) return '#1b0202'; // Soul sand valley base
    if (value < 0.42) return '#d83a12'; // Lava lakes
    if (value < 0.48) return '#570808'; // Magma blocks/basalt
    if (value < 0.8) return '#400606'; // Netherrack
    return '#8a1313'; // Crimson forest highlights
  } else if (dimension === 'minecraft:the_end') {
    if (value < 0.38) return '#050308'; // End Void
    if (value < 0.44) return '#130d1c'; // Void edge fog
    if (value < 0.82) return '#dedea4'; // End Stone
    return '#ececb3'; // Outer End island highlights
  } else {
    // Overworld
    if (value < 0.35) return '#0c1a2d'; // Deep Ocean
    if (value < 0.45) return '#142c4b'; // Ocean
    if (value < 0.48) return '#1b3c66'; // Rivers/shallow water
    if (value < 0.51) return '#bfae7e'; // Sandy beaches
    if (value < 0.64) return '#2e5c24'; // Plains/grass
    if (value < 0.76) return '#1b3b14'; // Dense forests
    if (value < 0.86) return '#4c5257'; // Stone mountain sides
    return '#ffffff'; // Snowy peaks
  }
}

interface MapWidgetProps {
  players: Player[];
  selectedPlayerName: string | null;
  onSelectPlayer: (name: string | null) => void;
}

function MapWidget({ players, selectedPlayerName, onSelectPlayer }: MapWidgetProps) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dimensionFilter, setDimensionFilter] = useState<string>('all');
  const [hoveredPlayer, setHoveredPlayer] = useState<Player | null>(null);
  const [isDraggingState, setIsDraggingState] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 350 });

  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastCenteredPlayerRef = useRef<string | null>(null);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);
  const lastPinchDistRef = useRef<number | null>(null);
  const renderTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const canvasRenderedRef = useRef({ panX: 0, panY: 0, zoom: 1 });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const onSelectPlayerRef = useRef(onSelectPlayer);
  onSelectPlayerRef.current = onSelectPlayer;

  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width || 600,
          height: 350,
        });
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const playersWithPosition = useMemo(() => {
    return players.filter((p) => p.position !== undefined);
  }, [players]);

  useEffect(() => {
    if (!selectedPlayerName) {
      lastCenteredPlayerRef.current = null;
      return;
    }
    const followedPlayer = playersWithPosition.find((p) => p.name === selectedPlayerName);
    if (
      followedPlayer &&
      followedPlayer.position &&
      lastCenteredPlayerRef.current !== selectedPlayerName
    ) {
      setPan({
        x: -followedPlayer.position.x,
        y: -followedPlayer.position.z,
      });
      lastCenteredPlayerRef.current = selectedPlayerName;
    }
  }, [selectedPlayerName, playersWithPosition]);

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    setIsDraggingState(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging.current) return;
    onSelectPlayer(null);
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    dragStart.current = { x: e.clientX, y: e.clientY };
    setPan((current) => ({
      x: current.x + dx / zoom,
      y: current.y + dy / zoom,
    }));
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    setIsDraggingState(false);
  };

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = 1.1;
      setZoom((z) => {
        const nextZoom = e.deltaY < 0 ? z * zoomFactor : z / zoomFactor;
        return Math.max(0.1, Math.min(20, nextZoom));
      });
    };

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      if (e.touches.length === 1 && t0) {
        lastTouchRef.current = { x: t0.clientX, y: t0.clientY };
        lastPinchDistRef.current = null;
      } else if (e.touches.length === 2 && t0 && t1) {
        lastPinchDistRef.current = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
        lastTouchRef.current = {
          x: (t0.clientX + t1.clientX) / 2,
          y: (t0.clientY + t1.clientY) / 2,
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      if (e.touches.length === 1 && t0 && lastTouchRef.current) {
        onSelectPlayerRef.current(null);
        const dx = t0.clientX - lastTouchRef.current.x;
        const dy = t0.clientY - lastTouchRef.current.y;
        lastTouchRef.current = { x: t0.clientX, y: t0.clientY };
        setPan((current) => ({
          x: current.x + dx / zoomRef.current,
          y: current.y + dy / zoomRef.current,
        }));
      } else if (e.touches.length === 2 && t0 && t1) {
        const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
        const center = {
          x: (t0.clientX + t1.clientX) / 2,
          y: (t0.clientY + t1.clientY) / 2,
        };
        if (lastPinchDistRef.current !== null) {
          const scale = dist / lastPinchDistRef.current;
          setZoom((z) => Math.max(0.1, Math.min(20, z * scale)));
        }
        if (lastTouchRef.current) {
          const dx = center.x - lastTouchRef.current.x;
          const dy = center.y - lastTouchRef.current.y;
          setPan((current) => ({
            x: current.x + dx / zoomRef.current,
            y: current.y + dy / zoomRef.current,
          }));
        }
        lastPinchDistRef.current = dist;
        lastTouchRef.current = center;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        lastTouchRef.current = null;
        lastPinchDistRef.current = null;
      } else if (e.touches.length === 1) {
        const t0 = e.touches[0];
        lastPinchDistRef.current = null;
        if (t0) {
          lastTouchRef.current = { x: t0.clientX, y: t0.clientY };
        }
      }
    };

    svgElement.addEventListener('wheel', handleWheel, { passive: false });
    svgElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    svgElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    svgElement.addEventListener('touchend', handleTouchEnd);
    return () => {
      svgElement.removeEventListener('wheel', handleWheel);
      svgElement.removeEventListener('touchstart', handleTouchStart);
      svgElement.removeEventListener('touchmove', handleTouchMove);
      svgElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = dimensions.width;
    const height = dimensions.height;

    // Apply CSS transform for instant visual feedback during pan/zoom
    const rendered = canvasRenderedRef.current;
    const scaleRatio = zoom / rendered.zoom;
    const panDx = (pan.x - rendered.panX) * zoom;
    const panDy = (pan.y - rendered.panY) * zoom;
    canvas.style.transform = `translate(${panDx}px, ${panDy}px) scale(${scaleRatio})`;

    // Debounce the expensive terrain re-render
    clearTimeout(renderTimerRef.current);
    renderTimerRef.current = setTimeout(() => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      let activeDimension = dimensionFilter;
      if (activeDimension === 'all') {
        const firstPlayer = playersWithPosition[0];
        activeDimension = firstPlayer?.position?.dimension ?? 'minecraft:overworld';
      }

      const cellSize = 6;
      const cols = Math.ceil(width / cellSize);
      const rows = Math.ceil(height / cellSize);

      const cx = width / 2;
      const cy = height / 2;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const px = c * cellSize + cellSize / 2;
          const py = r * cellSize + cellSize / 2;

          const wx = (px - cx) / zoom - pan.x;
          const wz = (py - cy) / zoom - pan.y;

          const freqScale = activeDimension === 'minecraft:the_nether' ? 0.008 : 0.0025;
          const n = fbm2D(wx * freqScale, wz * freqScale);

          ctx.fillStyle = getTerrainColor(n, activeDimension);
          ctx.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
        }
      }

      canvasRenderedRef.current = { panX: pan.x, panY: pan.y, zoom };
      canvas.style.transform = '';
    }, 120);

    return () => clearTimeout(renderTimerRef.current);
  }, [zoom, pan, dimensions, dimensionFilter, playersWithPosition]);

  const zoomIn = () => setZoom((z) => Math.min(20, z * 1.3));
  const zoomOut = () => setZoom((z) => Math.max(0.1, z / 1.3));
  const resetMap = () => {
    setZoom(1);
    if (playersWithPosition.length > 0 && playersWithPosition[0]?.position) {
      onSelectPlayer(playersWithPosition[0].name);
    } else {
      setPan({ x: 0, y: 0 });
      onSelectPlayer(null);
    }
  };

  const filteredPlayers = useMemo(() => {
    if (dimensionFilter === 'all') return playersWithPosition;
    return playersWithPosition.filter((p) => p.position?.dimension === dimensionFilter);
  }, [playersWithPosition, dimensionFilter]);

  const gridSpacing = zoom > 4 ? 50 : zoom > 1.5 ? 100 : zoom > 0.4 ? 500 : 1000;
  const cx = dimensions.width / 2;
  const cy = dimensions.height / 2;

  const minX = Math.floor((-cx / zoom - pan.x) / gridSpacing) * gridSpacing;
  const maxX = Math.ceil((cx / zoom - pan.x) / gridSpacing) * gridSpacing;
  const minZ = Math.floor((-cy / zoom - pan.y) / gridSpacing) * gridSpacing;
  const maxZ = Math.ceil((cy / zoom - pan.y) / gridSpacing) * gridSpacing;

  const gridLinesX = [];
  const maxGridLines = 100;
  let countX = 0;
  for (let x = minX; x <= maxX && countX < maxGridLines; x += gridSpacing) {
    gridLinesX.push(x);
    countX++;
  }

  const gridLinesZ = [];
  let countZ = 0;
  for (let z = minZ; z <= maxZ && countZ < maxGridLines; z += gridSpacing) {
    gridLinesZ.push(z);
    countZ++;
  }

  const scaleBarBlocks = (() => {
    const target = 80 / zoom;
    const mag = Math.pow(10, Math.floor(Math.log10(target)));
    const norm = target / mag;
    if (norm < 2) return mag;
    if (norm < 5) return 2 * mag;
    return 5 * mag;
  })();
  const scaleBarWidth = scaleBarBlocks * zoom;
  const scaleBarLabel =
    scaleBarBlocks >= 1000 ? `${scaleBarBlocks / 1000}K blocks` : `${scaleBarBlocks} blocks`;

  const getDimensionColor = (dimension?: string) => {
    switch (dimension) {
      case 'minecraft:the_nether':
        return 'var(--danger)';
      case 'minecraft:the_end':
        return 'var(--amber)';
      default:
        return 'var(--green)';
    }
  };

  const getDimensionLabel = (dimension?: string) => {
    switch (dimension) {
      case 'minecraft:the_nether':
        return 'Nether';
      case 'minecraft:the_end':
        return 'The End';
      default:
        return 'Overworld';
    }
  };

  return (
    <div className="map-widget-container" ref={containerRef}>
      <div className="map-header">
        <div className="map-title-row">
          <svg className="map-icon" viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="currentColor"
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            />
          </svg>
          <h3>Player Radar</h3>
        </div>
        <div className="map-controls-top">
          <select
            value={dimensionFilter}
            onChange={(e) => setDimensionFilter(e.target.value)}
            className="dimension-select"
          >
            <option value="all">All Dimensions</option>
            <option value="minecraft:overworld">Overworld</option>
            <option value="minecraft:the_nether">Nether</option>
            <option value="minecraft:the_end">The End</option>
          </select>
        </div>
      </div>

      <div className="map-viewport">
        <canvas
          ref={canvasRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        />
        <svg
          width="100%"
          height="100%"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          ref={svgRef}
          style={{
            position: 'relative',
            cursor: isDraggingState ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        >
          <g transform={`translate(${cx}, ${cy}) scale(${zoom}) translate(${pan.x}, ${pan.y})`}>
            {gridLinesX.map((x) => (
              <line
                key={`grid-x-${x}`}
                x1={x}
                y1={minZ}
                x2={x}
                y2={maxZ}
                stroke="var(--line)"
                strokeWidth={1 / zoom}
                strokeDasharray={x === 0 ? '0' : '4 4'}
                opacity={x === 0 ? 0.7 : 0.3}
              />
            ))}
            {gridLinesZ.map((z) => (
              <line
                key={`grid-z-${z}`}
                x1={minX}
                y1={z}
                x2={maxX}
                y2={z}
                stroke="var(--line)"
                strokeWidth={1 / zoom}
                strokeDasharray={z === 0 ? '0' : '4 4'}
                opacity={z === 0 ? 0.7 : 0.3}
              />
            ))}

            {gridLinesX.map((x) => {
              if (x === 0 || zoom < 0.3) return null;
              return (
                <text
                  key={`label-x-${x}`}
                  x={x}
                  y={-pan.y}
                  fill="var(--muted)"
                  fontSize={10 / zoom}
                  textAnchor="middle"
                  dy={-4 / zoom}
                  opacity={0.5}
                >
                  {x}
                </text>
              );
            })}
            {gridLinesZ.map((z) => {
              if (z === 0 || zoom < 0.3) return null;
              return (
                <text
                  key={`label-z-${z}`}
                  x={-pan.x}
                  y={z}
                  fill="var(--muted)"
                  fontSize={10 / zoom}
                  textAnchor="start"
                  dx={4 / zoom}
                  dy={3 / zoom}
                  opacity={0.5}
                >
                  {z}
                </text>
              );
            })}

            <g transform="translate(0, 0)">
              <g transform={`scale(${1 / zoom})`}>
                <circle
                  r="6"
                  fill="transparent"
                  stroke="var(--ink)"
                  strokeWidth="1.5"
                  opacity="0.6"
                />
                <line
                  x1="-8"
                  y1="0"
                  x2="8"
                  y2="0"
                  stroke="var(--ink)"
                  strokeWidth="1"
                  opacity="0.6"
                />
                <line
                  x1="0"
                  y1="-8"
                  x2="0"
                  y2="8"
                  stroke="var(--ink)"
                  strokeWidth="1"
                  opacity="0.6"
                />
              </g>
            </g>

            {filteredPlayers.map((player) => {
              if (!player.position) return null;
              const isFollowed = selectedPlayerName === player.name;
              const color = getDimensionColor(player.position.dimension);

              return (
                <g
                  key={player.name}
                  transform={`translate(${player.position.x}, ${player.position.z})`}
                  onMouseEnter={() => setHoveredPlayer(player)}
                  onMouseLeave={() => setHoveredPlayer(null)}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPlayer(player.name);
                  }}
                >
                  {isFollowed && (
                    <circle
                      r="16"
                      fill="none"
                      stroke={color}
                      strokeWidth="2"
                      opacity="0.8"
                      className="radar-pulse"
                      transform={`scale(${1 / zoom})`}
                    />
                  )}

                  <g transform={`scale(${1 / zoom})`}>
                    <circle r="10" fill="#081611" stroke={color} strokeWidth="2" />
                    <circle r="8" fill={color} opacity="0.2" />
                    <text
                      textAnchor="middle"
                      dy="3.5"
                      fill="var(--ink)"
                      fontSize="9"
                      fontWeight="bold"
                    >
                      {player.name.replace('.', '').slice(0, 2).toUpperCase()}
                    </text>

                    <g transform="translate(0, -16)">
                      <rect
                        x="-30"
                        y="-8"
                        width="60"
                        height="16"
                        rx="4"
                        fill="rgba(8, 22, 17, 0.85)"
                        stroke={isFollowed ? color : 'var(--line)'}
                        strokeWidth="1"
                      />
                      <text
                        textAnchor="middle"
                        dy="3"
                        fill="var(--ink)"
                        fontSize="8.5"
                        fontWeight={isFollowed ? 'bold' : 'normal'}
                      >
                        {player.name.length > 9 ? `${player.name.slice(0, 8)}…` : player.name}
                      </text>
                    </g>
                  </g>
                </g>
              );
            })}
          </g>
        </svg>

        <div className="map-hud-controls">
          <button onClick={zoomIn} title="Zoom In" className="hud-button">
            ＋
          </button>
          <button onClick={zoomOut} title="Zoom Out" className="hud-button">
            －
          </button>
          <button onClick={resetMap} title="Recenter / Reset" className="hud-button">
            ⟲
          </button>
        </div>

        <div className="map-compass">
          <div className="compass-arrow north">N</div>
          <div className="compass-arrow east">E</div>
          <div className="compass-arrow south">S</div>
          <div className="compass-arrow west">W</div>
        </div>

        <div className="map-scale">
          <div
            className="scale-bar"
            style={{ width: `${Math.max(20, Math.min(150, scaleBarWidth))}px` }}
          />
          <span>{scaleBarLabel}</span>
        </div>

        {hoveredPlayer?.position && (
          <div className="map-tooltip">
            <div className="tooltip-header">
              <span
                className="tooltip-dot"
                style={{ backgroundColor: getDimensionColor(hoveredPlayer.position.dimension) }}
              />
              <strong>{hoveredPlayer.name}</strong>
            </div>
            <div className="tooltip-coords">
              X: {Math.round(hoveredPlayer.position.x)} | Y: {Math.round(hoveredPlayer.position.y)}{' '}
              | Z: {Math.round(hoveredPlayer.position.z)}
            </div>
            <div className="tooltip-dimension">
              {getDimensionLabel(hoveredPlayer.position.dimension)}
            </div>
          </div>
        )}
      </div>

      <div className="map-sidebar">
        {playersWithPosition.length === 0 ? (
          <div className="sidebar-empty">Waiting for radar signals…</div>
        ) : (
          <div className="sidebar-players-list">
            {playersWithPosition.map((player) => {
              if (!player.position) return null;
              const isFollowed = selectedPlayerName === player.name;
              return (
                <button
                  key={player.name}
                  className={`sidebar-player-item ${isFollowed ? 'active' : ''}`}
                  onClick={() => onSelectPlayer(player.name)}
                >
                  <div className="sidebar-player-info">
                    <span
                      className="dimension-badge"
                      style={{
                        borderColor: getDimensionColor(player.position.dimension),
                        color: getDimensionColor(player.position.dimension),
                      }}
                    >
                      {getDimensionLabel(player.position.dimension)[0]}
                    </span>
                    <strong>{player.name}</strong>
                  </div>
                  <span className="sidebar-coords">
                    {Math.round(player.position.x)}, {Math.round(player.position.y)},{' '}
                    {Math.round(player.position.z)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function PlayersPanel({ server, mutate }: { server: ServerSummary; mutate: Mutation }) {
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [itemPlayer, setItemPlayer] = useState('');
  const [itemId, setItemId] = useState('');
  const [itemQuery, setItemQuery] = useState('');
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [amount, setAmount] = useState(64);
  const [modePlayer, setModePlayer] = useState('');
  const [playerMode, setPlayerMode] = useState<GameMode>('survival');
  const [mobPlayer, setMobPlayer] = useState('');

  const [selectedPlayerName, setSelectedPlayerName] = useState<string | null>(null);

  const playersWithPosition = useMemo(() => {
    return server.players.filter((p) => p.position !== undefined);
  }, [server.players]);

  useEffect(() => {
    if (selectedPlayerName === null && playersWithPosition.length > 0) {
      setSelectedPlayerName(playersWithPosition[0]?.name ?? null);
    }
  }, [playersWithPosition, selectedPlayerName]);
  const playerKey = server.players.map((player) => player.name).join('\n');
  useEffect(() => {
    const playerNames = playerKey ? playerKey.split('\n') : [];
    setSource((current) => (playerNames.includes(current) ? current : (playerNames[0] ?? '')));
    setDestination((current) =>
      playerNames.includes(current) ? current : (playerNames[1] ?? playerNames[0] ?? ''),
    );
    setItemPlayer((current) => (playerNames.includes(current) ? current : (playerNames[0] ?? '')));
    setModePlayer((current) => (playerNames.includes(current) ? current : (playerNames[0] ?? '')));
    setMobPlayer((current) => (playerNames.includes(current) ? current : (playerNames[0] ?? '')));
  }, [server.id, playerKey]);
  const selectedItem = MINECRAFT_ITEMS.find((item) => item.id === itemId);
  const itemMatches = useMemo(() => findMinecraftItems(itemQuery), [itemQuery]);

  if (!server.running)
    return <EmptyState title="This world is asleep" text="Start it to see players." />;
  if (!server.players.length)
    return <EmptyState title="Nobody is here yet" text="The world is online and waiting." />;

  return (
    <div className="players-container">
      <MapWidget
        players={server.players}
        selectedPlayerName={selectedPlayerName}
        onSelectPlayer={setSelectedPlayerName}
      />
      <div className="players-layout">
        <div className="player-list">
          {server.players.map((player) => (
            <PlayerRow
              player={player}
              key={player.name}
              isSelected={selectedPlayerName === player.name}
              onClick={() => setSelectedPlayerName(player.name)}
            />
          ))}
        </div>
        <div className="player-tools">
          <form
            className="player-tool-card"
            onSubmit={(event) => {
              event.preventDefault();
              mutate(
                `teleport-${server.id}`,
                () => api.teleport(server.id, source, destination),
                `${source} teleported to ${destination}`,
              );
            }}
          >
            <h3>Teleport player</h3>
            <label>
              Move
              <select value={source} onChange={(event) => setSource(event.target.value)}>
                {server.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <label>
              To
              <select value={destination} onChange={(event) => setDestination(event.target.value)}>
                {server.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <button
              className="primary-button"
              disabled={!source || !destination || source === destination}
            >
              Teleport
            </button>
          </form>

          <form
            className="player-tool-card"
            onSubmit={(event) => {
              event.preventDefault();
              if (!selectedItem) return;
              mutate(
                `give-${server.id}`,
                async () => {
                  await api.giveItem(
                    server.id,
                    itemPlayer,
                    selectedItem.id as MinecraftItemId,
                    amount,
                  );
                },
                `${amount} × ${selectedItem.name} given to ${itemPlayer}`,
              );
            }}
          >
            <h3>Give items</h3>
            <label>
              Player
              <select value={itemPlayer} onChange={(event) => setItemPlayer(event.target.value)}>
                {server.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <label>
              Item
              <div className="item-picker">
                <input
                  type="search"
                  role="combobox"
                  aria-expanded={itemPickerOpen}
                  aria-controls={`minecraft-items-${server.id}`}
                  value={itemQuery}
                  onFocus={() => setItemPickerOpen(true)}
                  onBlur={() => window.setTimeout(() => setItemPickerOpen(false), 120)}
                  onChange={(event) => {
                    setItemQuery(event.target.value);
                    setItemId('');
                    setItemPickerOpen(true);
                  }}
                  placeholder="Search e.g. diamond axe"
                  autoComplete="off"
                  required
                />
                {itemPickerOpen && (
                  <div className="item-options" id={`minecraft-items-${server.id}`} role="listbox">
                    {itemMatches.map((item) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected={item.id === itemId}
                        key={item.id}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setItemId(item.id);
                          setItemQuery(item.name);
                          setItemPickerOpen(false);
                        }}
                      >
                        <strong>{item.name}</strong>
                        <span>{item.id}</span>
                      </button>
                    ))}
                    {!itemMatches.length && <p>No matching items</p>}
                  </div>
                )}
              </div>
            </label>
            <label>
              Amount
              <input
                type="number"
                min="1"
                max="2304"
                inputMode="numeric"
                value={amount}
                onChange={(event) => setAmount(event.target.valueAsNumber)}
                required
              />
            </label>
            <button
              className="primary-button"
              disabled={
                !itemPlayer ||
                !selectedItem ||
                !Number.isInteger(amount) ||
                amount < 1 ||
                amount > 2304
              }
            >
              Give items
            </button>
          </form>

          <form
            className="player-tool-card"
            onSubmit={(event) => {
              event.preventDefault();
              mutate(
                `player-mode-${server.id}`,
                () => api.setPlayerMode(server.id, modePlayer, playerMode),
                `${modePlayer} changed to ${playerMode}`,
              );
            }}
          >
            <h3>Set player mode</h3>
            <p className="tool-hint">
              Changes this player now. The server default stays unchanged.
            </p>
            <label>
              Player
              <select value={modePlayer} onChange={(event) => setModePlayer(event.target.value)}>
                {server.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <label>
              Game mode
              <select
                value={playerMode}
                onChange={(event) => setPlayerMode(event.target.value as GameMode)}
              >
                {GAME_MODES.map((mode) => (
                  <option value={mode} key={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </label>
            <button className="primary-button" disabled={!modePlayer}>
              Set game mode
            </button>
          </form>

          <form
            className="player-tool-card danger-card"
            onSubmit={(event) => {
              event.preventDefault();
              if (
                !confirm(
                  `Kill all mobs within 15 blocks of ${mobPlayer}? This includes animals, villagers, pets, and hostile mobs.`,
                )
              ) {
                return;
              }
              mutate(
                `kill-mobs-${server.id}`,
                () => api.killNearbyMobs(server.id, mobPlayer),
                `Nearby mobs removed around ${mobPlayer}`,
              );
            }}
          >
            <h3>Clear nearby mobs</h3>
            <p className="tool-hint">
              Removes mobs within 15 blocks. Players, items, displays, vehicles, and projectiles are
              preserved.
            </p>
            <label>
              Center on player
              <select value={mobPlayer} onChange={(event) => setMobPlayer(event.target.value)}>
                {server.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <button className="danger-button" disabled={!mobPlayer}>
              Kill nearby mobs
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function PlayerRow({
  player,
  isSelected,
  onClick,
}: {
  player: Player;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div className={`player-row ${isSelected ? 'active' : ''}`} onClick={onClick}>
      <span className="player-face">{player.name.replace('.', '').slice(0, 2).toUpperCase()}</span>
      <div>
        <strong>{player.name}</strong>
        <span>{player.platform}</span>
      </div>
      <span className="online-label">online</span>
    </div>
  );
}

function WhitelistPanel({
  server,
  mutate,
  foregroundRefresh,
}: {
  server: ServerSummary;
  mutate: Mutation;
  foregroundRefresh: number;
}) {
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [username, setUsername] = useState('');
  const [platform, setPlatform] = useState<PlayerPlatform>('java');
  const load = useCallback(
    async () => setEntries((await api.whitelist(server.id)).entries),
    [server.id],
  );
  useEffect(() => {
    void load();
  }, [foregroundRefresh, load]);

  return (
    <div className="whitelist-layout">
      <form
        className="add-player"
        onSubmit={(event) => {
          event.preventDefault();
          mutate(
            `whitelist-${server.id}`,
            async () => {
              const result = await api.addWhitelist(server.id, username, platform);
              setEntries(result.entries);
              setUsername('');
            },
            `${username} added to the whitelist`,
          );
        }}
      >
        <label>
          Player name
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder={platform === 'bedrock' ? 'Xbox player name' : 'Java username'}
            disabled={!server.running}
            required
          />
        </label>
        <label>
          Edition
          <select
            value={platform}
            onChange={(event) => setPlatform(event.target.value as PlayerPlatform)}
            disabled={!server.running}
          >
            <option value="java">Java</option>
            <option value="bedrock">Bedrock</option>
          </select>
        </label>
        <p className="preview">
          Stored as{' '}
          <code>
            {platform === 'bedrock' ? '.' : ''}
            {username.trim().replaceAll(' ', '_') || 'player'}
          </code>
        </p>
        <button className="primary-button" disabled={!server.running}>
          Add player
        </button>
      </form>
      <div className="whitelist">
        {entries.map((entry) => (
          <div className="player-row" key={entry.name}>
            <span className="player-face">
              {entry.name.replace('.', '').slice(0, 2).toUpperCase()}
            </span>
            <div>
              <strong>{entry.name}</strong>
              <span>{entry.platform}</span>
            </div>
            <button
              className="icon-button"
              disabled={!server.running}
              aria-label={`Remove ${entry.name}`}
              onClick={() => {
                const rawName = entry.platform === 'bedrock' ? entry.name.slice(1) : entry.name;
                if (!confirm(`Remove ${entry.name} from the whitelist?`)) return;
                mutate(
                  `whitelist-${server.id}`,
                  async () => {
                    const result = await api.removeWhitelist(server.id, rawName, entry.platform);
                    setEntries(result.entries);
                  },
                  `${entry.name} removed from the whitelist`,
                );
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogsPanel({
  server,
  foregroundRefresh,
}: {
  server: ServerSummary;
  foregroundRefresh: number;
}) {
  const [lines, setLines] = useState<string[]>([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState<'all' | 'warn' | 'error'>('all');
  const cursor = useRef<number | undefined>(undefined);
  const refreshInFlight = useRef(false);
  useEffect(() => {
    cursor.current = undefined;
    setLines([]);
  }, [server.id]);
  useEffect(() => {
    let alive = true;
    async function refresh() {
      if (paused || refreshInFlight.current) return;
      refreshInFlight.current = true;
      try {
        const result = await api.logs(server.id, cursor.current);
        if (!alive) return;
        cursor.current = result.cursor;
        setLines((current) => [...current, ...result.lines].slice(-250));
      } catch {
        // The app-level status banner handles upstream availability.
      } finally {
        refreshInFlight.current = false;
      }
    }
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [foregroundRefresh, server.id, paused]);
  const visible = useMemo(
    () => lines.filter((line) => filter === 'all' || line.toLowerCase().includes(filter)),
    [filter, lines],
  );
  return (
    <div className="logs-panel">
      <div className="log-toolbar">
        <div>
          {(['all', 'warn', 'error'] as const).map((item) => (
            <button
              className={filter === item ? 'active' : ''}
              onClick={() => setFilter(item)}
              key={item}
            >
              {item}
            </button>
          ))}
        </div>
        <button onClick={() => setPaused(!paused)}>{paused ? 'Resume' : 'Pause'}</button>
      </div>
      <pre>{visible.length ? visible.join('\n') : 'No matching log lines.'}</pre>
    </div>
  );
}

function CommandPanel({
  server,
  mutate,
  busy,
}: {
  server: ServerSummary;
  mutate: Mutation;
  busy: string | null;
}) {
  const [command, setCommand] = useState('');
  const normalized = command.trim().replace(/^\//, '').trim();

  return (
    <form
      className="command-panel danger-card"
      onSubmit={(event) => {
        event.preventDefault();
        if (!normalized) return;
        if (
          !confirm(
            `Run this command on ${server.name}?\n\n${normalized}\n\nCustom commands can permanently alter the world or stop the server.`,
          )
        ) {
          return;
        }
        mutate(
          `command-${server.id}`,
          async () => {
            await api.runCommand(server.id, normalized);
            setCommand('');
          },
          'Command sent',
        );
      }}
    >
      <div>
        <p className="eyebrow">Advanced control</p>
        <h3>Run a server command</h3>
        <p className="tool-hint">
          Commands run with full console permissions. Enter one command without a leading slash.
        </p>
      </div>
      <label>
        Command
        <input
          type="text"
          value={command}
          onChange={(event) => setCommand(event.target.value.replaceAll(/[\r\n]/g, ''))}
          placeholder="time set day"
          maxLength={512}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          disabled={!server.running || busy !== null}
          required
        />
      </label>
      <p className="command-preview">
        <code>{normalized || 'command arguments'}</code>
        <span>{normalized.length}/512</span>
      </p>
      <button className="danger-button" disabled={!server.running || busy !== null || !normalized}>
        Run command
      </button>
    </form>
  );
}

function OwnerPanel({ mutate }: { mutate: Mutation }) {
  const [invite, setInvite] = useState<{ url: string; expiresAt: number } | null>(null);
  const [accounts, setAccounts] = useState<
    Array<{ id: string; displayName: string; isOwner: boolean; enabled: boolean }>
  >([]);
  const load = useCallback(async () => setAccounts((await api.accounts()).users), []);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <section className="owner-panel">
      <div>
        <p className="eyebrow">Owner tools</p>
        <h2>Family access</h2>
      </div>
      <button
        className="primary-button"
        onClick={() =>
          mutate(
            'invite',
            async () => setInvite(await api.createInvitation()),
            'Invitation created',
          )
        }
      >
        Create invitation
      </button>
      {invite && (
        <div className="invite-box">
          <input readOnly value={invite.url} />
          <button
            className="quiet-button"
            onClick={() => void navigator.clipboard.writeText(invite.url)}
          >
            Copy
          </button>
          <span>Expires {relativeTime(invite.expiresAt)}</span>
        </div>
      )}
      <div className="account-list">
        {accounts.map((account) => (
          <div className="player-row" key={account.id}>
            <span className="player-face">{account.displayName.slice(0, 2).toUpperCase()}</span>
            <div>
              <strong>{account.displayName}</strong>
              <span>{account.isOwner ? 'owner' : account.enabled ? 'active' : 'revoked'}</span>
            </div>
            {!account.isOwner && account.enabled && (
              <button
                className="quiet-button"
                onClick={() =>
                  mutate(
                    'revoke',
                    async () => {
                      await api.revokeAccount(account.id);
                      await load();
                    },
                    `${account.displayName} revoked`,
                  )
                }
              >
                Revoke
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty-state">
      <span>◇</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

interface AssistantPanelProps {
  user: SessionUser;
  mutate: Mutation;
  setToast: (toast: Toast | null) => void;
}

const MODELS = [
  { id: 'openai/gpt-5.6-luna', label: 'GPT Luna' },
  { id: 'qwen/qwen3.6-flash', label: 'Qwen 3.6 flash' },
  { id: 'z-ai/glm-5.2', label: 'GLM 5.2' },
  { id: 'google/gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { id: 'anthropic/claude-haiku-4.5', label: 'Haiku 4.5' },
];

function AssistantPanel({ user, mutate, setToast }: AssistantPanelProps) {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState(MODELS[0]!.id);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [liveTools, setLiveTools] = useState<
    Array<{
      id: string;
      name: string;
      input?: unknown;
      status: 'working' | 'done' | 'error';
      summary?: string;
    }>
  >([]);

  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [messagesData, settingsData] = await Promise.all([
        getAgentMessages(),
        getAgentSettings(),
      ]);
      setMessages(messagesData.messages);
      setSettings(settingsData);
    } catch (err) {
      console.error('Failed to load agent data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleScroll = () => {
    const container = chatLogRef.current;
    if (!container) return;
    const threshold = 15;
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    stickToBottomRef.current = isAtBottom;
  };

  useEffect(() => {
    if (stickToBottomRef.current && chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [messages, streamingText, liveTools]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputMessage.trim();
    if (!trimmed || streaming || !settings?.enabled) return;

    setInputMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setStreaming(true);
    setStreamingText('');
    setLiveTools([]);
    stickToBottomRef.current = true;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await streamAgentMessage(
        trimmed,
        selectedModel,
        {
          onMessage: (msg) => {
            if (msg.role === 'assistant') {
              setStreamingText('');
            }
            setMessages((prev) => {
              const map = new Map(prev.map((m) => [m.id, m]));
              map.set(msg.id, msg);
              return Array.from(map.values()).sort((a, b) => a.createdAt - b.createdAt);
            });
          },
          onToken: (text) => {
            setStreamingText((prev) => prev + text);
          },
          onToolStart: ({ id, name, input }) => {
            setLiveTools((prev) => [...prev, { id, name, input, status: 'working' }]);
          },
          onToolEnd: ({ id, ok, summary }) => {
            setLiveTools((prev) =>
              prev.map((t) => (t.id === id ? { ...t, status: ok ? 'done' : 'error', summary } : t)),
            );
          },
          onDone: () => {
            setStreaming(false);
            setStreamingText('');
            setLiveTools([]);
            abortControllerRef.current = null;
          },
          onError: (err: string) => {
            console.error('Agent stream error:', err);
            setToast({ kind: 'error', message: `Agent stream error: ${err}` });
          },
        },
        controller.signal,
      );
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error(err);
        setToast({ kind: 'error', message: `Agent error: ${err.message || String(err)}` });
      }
    } finally {
      setStreaming(false);
    }
  };

  const handleStop = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreaming(false);
    setStreamingText('');
    setLiveTools([]);
    try {
      const messagesData = await getAgentMessages();
      setMessages(messagesData.messages);
    } catch (err) {
      console.error('Failed to reload history after stop', err);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to clear the conversation history?')) {
      return;
    }
    try {
      await clearAgent();
      setMessages([]);
      setStreamingText('');
      setLiveTools([]);
    } catch (err: any) {
      console.error('Failed to clear conversation', err);
    }
  };

  const handleToggleEnabled = (enabled: boolean) => {
    mutate(
      'agent-settings',
      async () => {
        const updated = await setAgentEnabled(enabled);
        setSettings(updated);
      },
      `Assistant ${enabled ? 'enabled' : 'disabled'}`,
    );
  };

  const formatToolContent = (toolName: string, content: string) => {
    try {
      const data = JSON.parse(content);
      if (toolName === 'build_structure') {
        return `Sent: ${data.sent}, Skipped: ${data.skipped}${data.skippedSamples?.length ? ` (Skipped: ${data.skippedSamples.join(', ')})` : ''}`;
      }
      if (toolName === 'web_search') {
        return `Found ${data.results?.length ?? 0} results`;
      }
      if (toolName === 'list_servers') {
        return `Found ${data.length ?? 0} servers`;
      }
      if (data.error) {
        return `Error: ${data.error}`;
      }
      return typeof data === 'object' ? JSON.stringify(data) : String(data);
    } catch {
      return content;
    }
  };

  const getToolCallStatus = (
    callId: string,
  ): { status: string; summary: string; input?: unknown; result?: unknown } => {
    const live = liveTools.find((t) => t.id === callId);
    if (live) {
      return {
        status: live.status,
        summary: live.summary || (live.status === 'working' ? 'working...' : live.status),
        input: live.input,
      };
    }
    const toolMsg = messages.find((m) => m.role === 'tool' && m.toolCallId === callId);
    if (toolMsg) {
      return {
        status: 'done',
        summary: formatToolContent(toolMsg.toolName || '', toolMsg.content),
        input: undefined,
        result: tryParseJson(toolMsg.content),
      };
    }
    return { status: 'working', summary: 'working...', input: undefined };
  };

  if (loading) {
    return (
      <div className="assistant-panel" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div>Loading assistant...</div>
      </div>
    );
  }

  const isEnabled = settings?.enabled ?? false;

  return (
    <div className={`assistant-panel ${!isEnabled ? 'disabled' : ''}`}>
      <div className="assistant-header">
        <h2>Assistant</h2>
        <div className="assistant-header-actions">
          {isEnabled && (
            <select
              className="model-selector"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={streaming}
            >
              {MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
          {user.isOwner && settings && (
            <div className="agent-toggle-container">
              <span id="agent-toggle-label">Enable Assistant</span>
              <label className="switch" htmlFor="agent-toggle">
                <input
                  id="agent-toggle"
                  type="checkbox"
                  checked={settings.enabled}
                  aria-labelledby="agent-toggle-label"
                  onChange={(e) => handleToggleEnabled(e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
          )}
          <button
            className="quiet-button danger"
            onClick={handleClear}
            disabled={streaming || messages.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {!isEnabled ? (
        <div
          className="chat-log"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <div className="disabled-banner" role="alert">
            The assistant is currently disabled by an owner.
          </div>
        </div>
      ) : (
        <>
          <div className="chat-log" ref={chatLogRef} onScroll={handleScroll}>
            {messages.map((msg) => {
              if ((msg.role as string) === 'system') return null;
              if (msg.role === 'user') {
                return <UserMessageBubble key={msg.id} msg={msg} />;
              }
              if (msg.role === 'assistant') {
                return (
                  <AssistantMessageBubble
                    key={msg.id}
                    msg={msg}
                    getToolCallStatus={getToolCallStatus}
                  />
                );
              }
              if (msg.role === 'tool') return null;
              return null;
            })}

            {streamingText && (
              <div className="chat-bubble assistant">
                <div className="bubble-content">
                  {streamingText}
                  <span className="streaming-cursor" />
                </div>
              </div>
            )}

            {liveTools.map((tool) => (
              <LiveToolCard key={tool.id} tool={tool} />
            ))}
          </div>

          <form className="chat-input-container" onSubmit={handleSend}>
            <div className="chat-input">
              <textarea
                ref={textareaRef}
                rows={1}
                value={inputMessage}
                maxLength={2000}
                placeholder="Ask the assistant..."
                disabled={streaming}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend(e);
                  }
                }}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                  adjustTextareaHeight();
                }}
              />
              <div className="chat-input-buttons">
                {streaming ? (
                  <button type="button" className="quiet-button danger" onClick={handleStop}>
                    Stop
                  </button>
                ) : (
                  <button type="submit" className="quiet-button" disabled={!inputMessage.trim()}>
                    Send
                  </button>
                )}
              </div>
            </div>
            <div className="chat-input-actions">
              <span>{inputMessage.length}/2000 chars</span>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function formatTimestamp(ts: number) {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function prettyJson(value: unknown) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function tryParseJson(str: string): unknown {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}

function UserMessageBubble({ msg }: { msg: AgentMessage }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`chat-bubble user ${expanded ? 'expanded' : ''}`}
      onClick={() => setExpanded((e) => !e)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((x) => !x);
        }
      }}
    >
      <div className="bubble-meta">
        <span>{msg.userDisplayName || 'User'}</span>
        <span className="bubble-timestamp">{formatTimestamp(msg.createdAt)}</span>
      </div>
      <div className="bubble-content">{msg.content}</div>
      {expanded && (
        <div className="bubble-details">
          <div className="detail-row">
            <span className="detail-label">User ID</span>
            <code>{msg.userId || '—'}</code>
          </div>
          <div className="detail-row">
            <span className="detail-label">Message ID</span>
            <code>{msg.id}</code>
          </div>
        </div>
      )}
      <div className="expand-hint">{expanded ? '▲ hide details' : '▼ details'}</div>
    </div>
  );
}

function AssistantMessageBubble({
  msg,
  getToolCallStatus,
}: {
  msg: AgentMessage;
  getToolCallStatus: (callId: string) => {
    status: string;
    summary: string;
    input?: unknown;
    result?: unknown;
  };
}) {
  const [expanded, setExpanded] = useState(false);
  const tokenInfo =
    msg.tokensIn !== undefined || msg.tokensOut !== undefined
      ? `${msg.tokensIn ?? '?'} in / ${msg.tokensOut ?? '?'} out`
      : null;
  return (
    <div
      className={`chat-bubble assistant ${expanded ? 'expanded' : ''}`}
      onClick={() => setExpanded((e) => !e)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((x) => !x);
        }
      }}
    >
      <div className="bubble-meta">
        {msg.model && <span className="bubble-model">{msg.model}</span>}
        {tokenInfo && <span className="bubble-tokens">{tokenInfo}</span>}
        <span className="bubble-timestamp">{formatTimestamp(msg.createdAt)}</span>
      </div>
      {msg.content ? (
        <div className="bubble-content">{msg.content}</div>
      ) : (
        <div className="bubble-content muted-text">(no text — tool calls only)</div>
      )}
      {msg.toolCalls && msg.toolCalls.length > 0 && (
        <div className="tool-cards-list">
          {msg.toolCalls.map((call) => {
            const statusInfo = getToolCallStatus(call.id);
            return (
              <ToolCard
                key={call.id}
                name={call.function.name}
                status={statusInfo.status}
                summary={statusInfo.summary}
                args={call.function.arguments}
                input={statusInfo.input}
                result={statusInfo.result}
                expanded={expanded}
              />
            );
          })}
        </div>
      )}
      {expanded && (
        <div className="bubble-details">
          <div className="detail-row">
            <span className="detail-label">Message ID</span>
            <code>{msg.id}</code>
          </div>
          {msg.model && (
            <div className="detail-row">
              <span className="detail-label">Model</span>
              <code>{msg.model}</code>
            </div>
          )}
          {msg.tokensIn !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Tokens in</span>
              <code>{msg.tokensIn}</code>
            </div>
          )}
          {msg.tokensOut !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Tokens out</span>
              <code>{msg.tokensOut}</code>
            </div>
          )}
          {msg.toolCalls && msg.toolCalls.length > 0 && (
            <div className="detail-section">
              <span className="detail-label">Tool call IDs</span>
              {msg.toolCalls.map((tc) => (
                <div key={tc.id} className="detail-row indent">
                  <code>{tc.id}</code>
                  <span className="detail-sub">→ {tc.function.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="expand-hint">{expanded ? '▲ hide details' : '▼ details'}</div>
    </div>
  );
}

function ToolCard({
  name,
  status,
  summary,
  args,
  input,
  result,
  expanded,
}: {
  name: string;
  status: string;
  summary: string;
  args: string;
  input?: unknown;
  result?: unknown;
  expanded: boolean;
}) {
  const [selfExpanded, setSelfExpanded] = useState(false);
  const showDetails = expanded || selfExpanded;
  const parsedArgs = input ?? tryParseJson(args);
  return (
    <div
      className={`tool-card ${status}`}
      role="status"
      aria-live="polite"
      onClick={(e) => {
        e.stopPropagation();
        setSelfExpanded((x) => !x);
      }}
    >
      <span className="tool-icon">🛠</span>
      <div className="tool-body">
        <div className="tool-header-row">
          <strong>{name}</strong>
          <span className={`tool-badge ${status}`}>{status}</span>
        </div>
        <span className="tool-status-text">{summary}</span>
        {showDetails && (
          <div className="tool-details">
            <div className="detail-section">
              <span className="detail-label">Arguments</span>
              <pre className="json-block">{prettyJson(parsedArgs)}</pre>
            </div>
            {result !== undefined && (
              <div className="detail-section">
                <span className="detail-label">Full result</span>
                <pre className="json-block">{prettyJson(result)}</pre>
              </div>
            )}
          </div>
        )}
        <div className="expand-hint-inline">{showDetails ? '▲ hide' : '▼ details'}</div>
      </div>
    </div>
  );
}

function LiveToolCard({
  tool,
}: {
  tool: {
    id: string;
    name: string;
    input?: unknown;
    status: 'working' | 'done' | 'error';
    summary?: string;
  };
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`tool-card ${tool.status} ${expanded ? 'expanded' : ''}`}
      role="status"
      aria-live="polite"
      onClick={() => setExpanded((e) => !e)}
    >
      <span className="tool-icon">🛠</span>
      <div className="tool-body">
        <div className="tool-header-row">
          <strong>{tool.name}</strong>
          <span className={`tool-badge ${tool.status}`}>{tool.status}</span>
        </div>
        <span className="tool-status-text">
          {tool.status === 'working' ? 'working...' : tool.summary || tool.status}
        </span>
        {expanded && tool.input !== undefined && (
          <div className="tool-details">
            <div className="detail-section">
              <span className="detail-label">Arguments</span>
              <pre className="json-block">{prettyJson(tool.input)}</pre>
            </div>
          </div>
        )}
        {tool.input !== undefined && (
          <div className="expand-hint-inline">{expanded ? '▲ hide' : '▼ details'}</div>
        )}
      </div>
    </div>
  );
}
